/**
 * Recurring invoices (design I): every schedule for the shop with its next
 * run, mode and channels, and Pause / Resume, Run now and End. "+ New
 * schedule" starts one from a recent issued invoice through the Make
 * recurring modal. Every change goes to the server and the list is fetched
 * again, so nothing on screen is decided locally.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import InvoiceRepository, { InvoiceListItem, Recurring } from '../../../internals/repository/InvoiceRepository';
import { useSelectedShop } from '../useInvoicing';
import { money } from '../format';
import { glyphOf } from '../deliveryModel';
import { ChannelGlyph } from '../components/DeliveryTag';
import { Dialog, ErrorBanner } from '../components/VoidModal';
import MakeRecurringModal from '../components/MakeRecurringModal';
import { errorText } from '../detailModel';
import {
  RECURRING_FORBIDDEN, STATUS_CHIP, customerName, frequencyLabel, isForbidden, modeLabel, nextRunText,
  recurringAmount, recurringCustomer, recurringMeta, sendOrder, sortRecurring, type RecurringSource,
} from '../recurringModel';

const GRID = 'minmax(220px,1.5fr) 130px 100px 120px 120px 90px 100px 230px';
const capsCls = 'text-[11px] font-semibold uppercase tracking-[.07em] text-text3';
const shell = 'h-screen flex flex-col w-full p-5 md:px-8 md:py-7 overflow-auto [&>*]:shrink-0 gap-5';
const menuItem = 'flex w-full items-center justify-between gap-2.5 rounded-lg border-0 bg-transparent px-2.5 py-2 text-left text-[13px] text-text1 hover:bg-surface2 focus-visible:bg-surface2 focus-visible:outline-none';
const NEW_FROM = 10;

interface Toast { text: string; docId?: string; number?: string | null }

export default function RecurringView() {
  const shop = useSelectedShop();
  const shopId: string = shop?.shopId ?? '';
  const cur: string = shop?.currency || 'LKR';

  const [items, setItems] = useState<Recurring[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<any>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [toast, setToast] = useState<Toast | null>(null);
  const [ending, setEnding] = useState<Recurring | null>(null);

  const [menuOpen, setMenuOpen] = useState(false);
  const [recent, setRecent] = useState<InvoiceListItem[] | null>(null);
  const [recentError, setRecentError] = useState<string | null>(null);
  const [from, setFrom] = useState<RecurringSource | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const flash = useCallback((t: Toast) => {
    setToast(t);
    window.setTimeout(() => setToast((x) => (x === t ? null : x)), t.docId ? 6000 : 3000);
  }, []);

  const load = useCallback(async (quiet = false) => {
    if (!shopId) return;
    if (!quiet) setLoading(true);
    try {
      const res = await InvoiceRepository.recurring(shopId);
      setItems(sortRecurring(res.items || []));
      setLoadError(null);
    } catch (e) {
      setLoadError(e);
      if (!quiet) setItems([]);
    } finally {
      setLoading(false);
    }
  }, [shopId]);

  useEffect(() => {
    setProblem(null);
    setRecent(null);
    load();
  }, [load]);

  // "+ New schedule" menu: Escape or a click outside closes it.
  useEffect(() => {
    if (!menuOpen) return undefined;
    const onDown = (e: MouseEvent) => { if (!menuRef.current?.contains(e.target as Node)) setMenuOpen(false); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setMenuOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [menuOpen]);

  const loadRecent = useCallback(async () => {
    setRecentError(null);
    try {
      const res = await InvoiceRepository.list(shopId, { docType: 'INVOICE', tab: 'ALL', limit: 40 });
      setRecent((res.items || []).filter((d) => d.number && d.displayStatus !== 'DRAFT' && d.displayStatus !== 'VOID').slice(0, NEW_FROM));
    } catch (e: any) {
      setRecentError(errorText(e));
    }
  }, [shopId]);

  const toggleMenu = () => {
    const next = !menuOpen;
    setMenuOpen(next);
    if (next && !recent) loadRecent();
  };

  const run = async (key: string, fn: () => Promise<void>) => {
    if (busy) return;
    setBusy(key);
    setProblem(null);
    try {
      await fn();
    } catch (e: any) {
      setProblem(isForbidden(e) ? RECURRING_FORBIDDEN : errorText(e, {
        invalid_state: 'This schedule has changed. The list has been refreshed.',
      }));
      if (e?.code === 'invalid_state' || e?.status === 404) load(true);
    } finally {
      setBusy(null);
    }
  };

  const setStatus = (r: Recurring, status: 'ACTIVE' | 'PAUSED') => run(`${r.recurringId}:status`, async () => {
    await InvoiceRepository.updateRecurring(shopId, r.recurringId, { status });
    await load(true);
    flash({ text: status === 'PAUSED' ? 'Schedule paused' : 'Schedule resumed' });
  });

  const runNow = (r: Recurring) => run(`${r.recurringId}:run`, async () => {
    const res = await InvoiceRepository.runRecurring(shopId, r.recurringId);
    await load(true);
    const d = res.document;
    const draft = d?.status === 'DRAFT';
    flash({
      text: draft ? 'Draft created' : `${d?.number ?? 'Invoice'} issued${r.mode === 'ISSUE_AND_SEND' && (r.channels?.length ?? 0) > 0 ? ' and sent' : ''}`,
      docId: d?.docId,
      number: d?.number,
    });
  });

  const endNow = (r: Recurring) => run(`${r.recurringId}:end`, async () => {
    await InvoiceRepository.endRecurring(shopId, r.recurringId);
    setEnding(null);
    await load(true);
    flash({ text: 'Schedule ended. Invoices already made are kept.' });
  });

  if (!shopId) {
    return <div className={shell}><p className="text-text2">Select a shop to see its recurring invoices.</p></div>;
  }

  const forbidden = isForbidden(loadError);
  const empty = !loading && !loadError && items.length === 0;

  return (
    <div className={shell}>
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex flex-col gap-1">
          <h1 className="m-0 text-[28px] font-semibold tracking-[-0.015em] text-text1">Recurring invoices</h1>
          <span className="text-[14px] text-text2">Schedules that create invoices for regular customers.</span>
        </div>
        <div className="relative" ref={menuRef}>
          <button type="button" className="nl-btn nl-btn--primary" aria-haspopup="menu" aria-expanded={menuOpen} onClick={toggleMenu}>
            + New schedule ▾
          </button>
          {menuOpen && (
            <div
              role="menu"
              aria-label="Start from an invoice"
              className="absolute right-0 top-[calc(100%+6px)] z-30 flex w-[340px] max-w-[calc(100vw-40px)] flex-col rounded-xl border border-borderc2 bg-surface p-1.5"
              style={{ boxShadow: 'var(--shadowLg)' }}
            >
              <span className="px-2.5 pb-1 pt-1.5 text-[11px] font-semibold uppercase tracking-[.07em] text-text3">Start from an invoice</span>
              {!recent && !recentError && [0, 1, 2].map((i) => (
                <span key={i} className="mx-2.5 my-2 h-3.5 animate-pulse rounded bg-surface3" aria-hidden />
              ))}
              {recentError && (
                <div className="flex flex-col items-start gap-2 px-2.5 py-2 text-[13px] text-text2">
                  {recentError}
                  <button type="button" className="nl-btn nl-btn--xs nl-btn--secondary" onClick={loadRecent}>Try again</button>
                </div>
              )}
              {recent && recent.length === 0 && (
                <span className="px-2.5 py-2 text-[13px] text-text2">No issued invoices yet. Issue an invoice first, then make it recurring.</span>
              )}
              {recent?.map((d) => (
                <button
                  key={d.docId}
                  role="menuitem"
                  type="button"
                  className={menuItem}
                  onClick={() => {
                    setMenuOpen(false);
                    setFrom({ docId: d.docId, number: d.number, customer: d.customer, totalMinor: d.totalMinor });
                  }}
                >
                  <span className="min-w-0 truncate">
                    <span className="font-mono">{d.number}</span>
                    {' · '}{customerName(d.customer)}
                  </span>
                  <span className="nl-num shrink-0 tabular-nums text-text3">{money(d.totalMinor, cur)}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </header>

      {problem && (
        <ErrorBanner text={problem}>
          <div className="mt-2">
            <button type="button" className="nl-btn nl-btn--xs nl-btn--ghost" onClick={() => setProblem(null)}>Dismiss</button>
          </div>
        </ErrorBanner>
      )}

      <div className="nl-card max-w-[1360px] overflow-x-auto p-0">
        <div className="min-w-[1110px]" role="table" aria-label="Recurring schedules">
          <div role="row" className={`grid items-center gap-3 bg-surface2 px-[18px] py-2.5 ${capsCls}`} style={{ gridTemplateColumns: GRID }}>
            <span role="columnheader">Customer · Schedule</span>
            <span role="columnheader" className="text-right">Amount</span>
            <span role="columnheader">Frequency</span>
            <span role="columnheader">Next run</span>
            <span role="columnheader">Mode</span>
            <span role="columnheader">Channels</span>
            <span role="columnheader">Status</span>
            <span role="columnheader"><span className="sr-only">Actions</span></span>
          </div>

          {loading && [0, 1, 2].map((i) => (
            <div key={i} className="border-t border-borderc px-[18px] py-5" aria-hidden>
              <span className="block h-2.5 w-1/2 animate-pulse rounded bg-surface3" />
            </div>
          ))}

          {!loading && loadError && (
            <div className="flex flex-col items-center gap-2 border-t border-borderc px-5 py-10 text-center">
              <span className="font-semibold text-text1">{forbidden ? RECURRING_FORBIDDEN : 'Schedules could not load'}</span>
              {!forbidden && <span className="text-[13px] text-text2">{errorText(loadError)}</span>}
              {!forbidden && <button type="button" className="nl-btn nl-btn--sm nl-btn--secondary" onClick={() => load()}>Try again</button>}
            </div>
          )}

          {empty && (
            <div className="flex flex-col items-center gap-1.5 border-t border-borderc px-5 py-12 text-center">
              <span className="text-[16px] font-semibold text-text1">No recurring invoices</span>
              <span className="max-w-[420px] text-text2">
                Open any invoice and choose More, then Make recurring, or use New schedule, to bill a customer every week or month.
              </span>
            </div>
          )}

          {!loading && !loadError && items.map((r) => {
            const chip = STATUS_CHIP[r.status] ?? STATUS_CHIP.PAUSED;
            const amount = recurringAmount(r);
            const channels = r.mode === 'ISSUE_AND_SEND' ? sendOrder(r.channels ?? []) : [];
            const ended = r.status === 'ENDED';
            return (
              <div
                key={r.recurringId}
                role="row"
                className={`grid items-center gap-3 border-t border-borderc px-[18px] py-[13px] ${ended ? 'opacity-70' : ''}`}
                style={{ gridTemplateColumns: GRID }}
              >
                <span role="cell" className="flex min-w-0 flex-col">
                  <span className="truncate font-semibold text-text1">{recurringCustomer(r)}</span>
                  <span className="truncate text-[12px] text-text3">
                    {r.name} · {recurringMeta(r)}
                    {r.lastDocId && (
                      <>
                        {' · '}
                        <Link to={`/invoices/${encodeURIComponent(r.lastDocId)}`} className="text-text3 hover:text-brand">Last invoice</Link>
                      </>
                    )}
                  </span>
                </span>
                <span role="cell" className="nl-num text-right font-semibold tabular-nums text-text1">{amount == null ? '—' : money(amount, (r as any).currency || cur)}</span>
                <span role="cell" className="text-[13px] text-text1">{frequencyLabel(r.frequency)}</span>
                <span role="cell" className="text-[13px]" style={{ color: r.status === 'ACTIVE' ? 'var(--text)' : 'var(--text3)' }}>{nextRunText(r)}</span>
                <span role="cell" className="text-[13px] text-text2">{modeLabel(r.mode)}</span>
                <span role="cell" className="flex flex-wrap gap-1">
                  {channels.length
                    ? channels.map((c) => <ChannelGlyph key={c} glyph={glyphOf(c)} size={20} title={glyphOf(c).name} />)
                    : <span className="text-[12px] text-text3">Not sent</span>}
                </span>
                <span role="cell">
                  <span
                    className="inline-flex h-[22px] items-center gap-1.5 whitespace-nowrap rounded-full px-[9px] text-[12px] font-semibold"
                    style={{ background: chip.bg, color: chip.fg }}
                  >
                    <span className="h-1.5 w-1.5 rounded-full" style={{ background: chip.dot }} />
                    {chip.label}
                  </span>
                </span>
                <span role="cell" className="flex justify-end gap-1.5">
                  {!ended && (
                    <>
                      <button
                        type="button"
                        className="nl-btn nl-btn--xs nl-btn--secondary"
                        disabled={!!busy}
                        onClick={() => setStatus(r, r.status === 'ACTIVE' ? 'PAUSED' : 'ACTIVE')}
                      >
                        {busy === `${r.recurringId}:status` ? '…' : r.status === 'ACTIVE' ? 'Pause' : 'Resume'}
                      </button>
                      <button type="button" className="nl-btn nl-btn--xs nl-btn--secondary" disabled={!!busy} onClick={() => runNow(r)}>
                        {busy === `${r.recurringId}:run` ? 'Running…' : 'Run now'}
                      </button>
                      <button
                        type="button"
                        className="nl-btn nl-btn--xs nl-btn--ghost"
                        disabled={!!busy}
                        onClick={() => setEnding(r)}
                        aria-label={`End ${r.name}`}
                      >
                        End
                      </button>
                    </>
                  )}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {ending && (
        <Dialog
          title={`End ${ending.name}`}
          sub="No more invoices are made from this schedule. Invoices already made are kept. This cannot be undone."
          onClose={() => setEnding(null)}
          busy={busy === `${ending.recurringId}:end`}
          footer={(
            <>
              <button type="button" className="nl-btn nl-btn--secondary" onClick={() => setEnding(null)} disabled={!!busy}>Cancel</button>
              <button type="button" className="nl-btn nl-btn--danger" onClick={() => endNow(ending)} disabled={!!busy}>
                {busy === `${ending.recurringId}:end` ? 'Ending…' : 'End schedule'}
              </button>
            </>
          )}
        />
      )}

      <MakeRecurringModal
        open={!!from}
        onClose={() => setFrom(null)}
        shopId={shopId}
        fromDoc={from}
        onCreated={() => {
          setFrom(null);
          load(true);
          flash({ text: 'Recurring schedule started' });
        }}
      />

      {toast && (
        <div
          role="status"
          className="fixed bottom-6 left-1/2 z-[1100] flex -translate-x-1/2 items-center gap-2 rounded-[10px] px-4 py-2.5 text-[14px] font-medium text-white"
          style={{ background: '#131318', boxShadow: 'var(--shadowLg)' }}
        >
          <span aria-hidden style={{ color: '#45D39F' }}>✓</span>
          {toast.text}
          {toast.docId && (
            <Link to={`/invoices/${encodeURIComponent(toast.docId)}`} className="ml-1 font-semibold underline" style={{ color: '#A79FFF' }}>
              Open {toast.number ?? 'invoice'}
            </Link>
          )}
        </div>
      )}
    </div>
  );
}
